import AccountService from "./AccountService.js";
import PlayerIdentity from "./PlayerIdentity.js";

/**
 * Notifications push du Défi du jour (46 du TODO), avec ou sans compte.
 *
 * Toujours à la demande du joueur : un bouton, puis l'autorisation du
 * navigateur. L'abonnement part au serveur avec la langue, le fuseau et l'heure
 * choisie ; le serveur n'envoie rien si le défi du jour est déjà joué (score
 * anonyme de l'appareil, ou sauvegarde du compte). Se désactive au même
 * endroit, ou en bloquant les notifications du site dans le navigateur.
 *
 * Sur iPhone, Safari n'expose le push qu'à une application installée sur
 * l'écran d'accueil : `isSupported()` y est faux tant que ce n'est pas le cas.
 */
const HOUR_KEY = "geonerd.pushHour";
export const DEFAULT_HOUR = 18;

let keyPromise = null;

export function isSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

export function permission() {
    return isSupported() ? Notification.permission : "unsupported";
}

/** Clé publique VAPID du serveur, ou `null` si le push n'y est pas configuré. */
export function serverKey() {
    if (!keyPromise) {
        keyPromise = AccountService.pushKey().then(({ ok, data }) => (ok && data?.publicKey ? data.publicKey : null));
    }
    return keyPromise;
}

/** Le push est-il proposable sur cet appareil (navigateur et serveur) ? */
export async function isAvailable() {
    if (!isSupported() || permission() === "denied") return false;
    return Boolean(await serverKey());
}

export async function currentSubscription() {
    if (!isSupported()) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    return registration ? registration.pushManager.getSubscription() : null;
}

export function storedHour() {
    const hour = parseInt(localStorage.getItem(HOUR_KEY), 10);
    return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : DEFAULT_HOUR;
}

function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

/**
 * Demande l'autorisation si besoin, abonne l'appareil et enregistre l'abonnement.
 * @returns {Promise<"subscribed"|"denied"|"unavailable"|"error">}
 */
export async function subscribe(hour = storedHour()) {
    const key = await serverKey();
    if (!isSupported() || !key) return "unavailable";
    const granted = await Notification.requestPermission();
    if (granted !== "granted") return "denied";
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription()
            || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
        const { ok } = await AccountService.pushSubscribe({
            subscription: subscription.toJSON(),
            device: PlayerIdentity.getOrCreateId(),
            lang: window.geoNerdApp.languageManager.currentLang === "en" ? "en" : "fr",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            hour,
        });
        if (!ok) return "error";
        localStorage.setItem(HOUR_KEY, String(hour));
        return "subscribed";
    } catch (e) {
        return "error";
    }
}

export async function unsubscribe() {
    const subscription = await currentSubscription();
    if (!subscription) return;
    await AccountService.pushUnsubscribe(subscription.endpoint);
    await subscription.unsubscribe().catch(() => {});
}

/** Nouvelle heure de rappel pour un appareil déjà abonné (même abonnement, heure mise à jour). */
export async function updateHour(hour) {
    localStorage.setItem(HOUR_KEY, String(hour));
    if (await currentSubscription()) return subscribe(hour);
    return "unavailable";
}
